/**
 * Тип callback функции для обновления
 */
export type UpdateCallback = () => void;

/**
 * Приоритет выполнения обновления
 */
export type UpdatePriority = 'high' | 'normal' | 'low';

/**
 * Интерфейс для запланированного обновления
 */
export interface ScheduledUpdate {
  id: string;
  callback: UpdateCallback;
  priority: UpdatePriority;
}

/**
 * Composable для батчинга визуальных обновлений через requestAnimationFrame.
 * Группирует множественные обновления в один animation frame для предотвращения layout thrashing.
 * 
 * @returns Объект с методами для планирования и управления обновлениями
 * 
 * @example
 * ```typescript
 * const { schedule, cancel, cancelAll } = useRAFScheduler();
 * 
 * // Запланировать обновление с нормальным приоритетом
 * schedule('update-indicator', () => {
 *   updateIndicatorPosition();
 * });
 * 
 * // Запланировать обновление с высоким приоритетом
 * schedule('measure-all', () => {
 *   measureAllElements();
 * }, 'high');
 * 
 * // Отменить конкретное обновление
 * cancel('update-indicator');
 * 
 * // Отменить все обновления (при unmount)
 * cancelAll();
 * ```
 */
export function useRAFScheduler() {
  const pendingUpdates = new Map<string, ScheduledUpdate>();
  let rafId: number | null = null;

  /**
   * Планирует выполнение callback в следующем animation frame.
   * Если обновление с таким id уже запланировано, оно будет заменено.
   * Все обновления выполняются в порядке приоритета: high → normal → low.
   * 
   * @param id - Уникальный идентификатор обновления
   * @param callback - Функция для выполнения
   * @param priority - Приоритет выполнения (по умолчанию 'normal')
   */
  const schedule = (
    id: string,
    callback: UpdateCallback,
    priority: UpdatePriority = 'normal'
  ): void => {
    pendingUpdates.set(id, { id, callback, priority });

    if (rafId === null) {
      rafId = requestAnimationFrame(flush);
    }
  };

  /**
   * Выполняет все запланированные обновления в порядке приоритета.
   * Вызывается автоматически через requestAnimationFrame.
   * Обрабатывает ошибки в callbacks, чтобы одна ошибка не блокировала остальные обновления.
   */
  const flush = (): void => {
    // Сортировка обновлений по приоритету
    const priorityOrder: Record<UpdatePriority, number> = { 
      high: 0, 
      normal: 1, 
      low: 2 
    };
    
    const updates = Array.from(pendingUpdates.values()).sort((a, b) => {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    pendingUpdates.clear();
    rafId = null;

    // Выполнение всех callbacks в одном frame
    for (const update of updates) {
      try {
        update.callback();
      } catch (error) {
        if (!process.env.SERVER) {
          console.error(`[RAF Scheduler] Update error [${update.id}]:`, error);
        }
      }
    }
  };

  /**
   * Отменяет запланированное обновление по его id.
   * Если это было последнее обновление, отменяет requestAnimationFrame.
   * 
   * @param id - Идентификатор обновления для отмены
   */
  const cancel = (id: string): void => {
    if (process.env.SERVER) return;
    
    pendingUpdates.delete(id);
    
    if (pendingUpdates.size === 0 && rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  /**
   * Отменяет все запланированные обновления.
   * Используется при unmount компонента для предотвращения утечек памяти.
   */
  const cancelAll = (): void => {
    pendingUpdates.clear();
    
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  return {
    schedule,
    cancel,
    cancelAll
  };
}
